import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal, { SweetAlertResult } from 'sweetalert2';

import ErrorMessage from 'classes/StandingData/ErrorMessage/Search/ErrorMessage';
import ErrorMessageList from 'classes/StandingData/ErrorMessage/Search/ErrorMessageList';
import Search from 'classes/StandingData/ErrorMessage/Search/Search';
import ErrorMessageDetailComponent from 'components/desktop/StandingData/ErrorMessage/Detail/ErrorMessageDetailComponent';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import StandingDataList, {
	Row,
} from 'components/desktop/_general/StandingDataList/StandingDataList';
import clone from 'functions/clone';
import { QueryParams, getQueryParams } from 'functions/getQueryParams';
import useAirNavigation from 'hooks/useAirNavigation';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { ISelectItem } from 'interfaces/ISelectItem';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	archive?: boolean;
}

const maxNumberOfButtons = 5;

const ErrorMessageManagementComponent = (props: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { params } = useAirNavigation();
	const displayOverlay = params.get('id') ? true : false;

	const [errorMessageList, setErrorMessageList] = useState<ErrorMessageList>(
		new ErrorMessageList()
	);
	const [search, setSearch] = useState<Search>(new Search());
	const [searchTerm, setSearchTerm] = useState<string>(search.searchTerm.value ?? '');

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode]);

	const initialize = async (): Promise<void> => {
		if (props.archive !== undefined && props.archive == true) {
			search.showOnlyDeleted = true;
		}
		if (window.location.href.includes('?')) {
			const queryParams: QueryParams = getQueryParams();
			search.mapFromUrl(queryParams);
			await getErrorMessages();
		} else {
			await getErrorMessages();
		}
		setSearch(clone(search));
	};

	const getErrorMessages = async () => {
		setIsLoading(true);

		const response = await errorMessageList.callApi(search.mapToRequest());

		response.do((x) => {
			errorMessageList.mapFromApi(x);
			setErrorMessageList(clone(errorMessageList));
		});
		setIsLoading(false);
	};

	const getSuggestedErrorMessages = async (): Promise<ISelectItem[]> => {
		if (searchTerm.length >= 1) {
			await errorMessageList.search(searchTerm, props.archive ?? false);
		} else {
			errorMessageList.filtered = [];
		}
		setErrorMessageList(clone(errorMessageList));
		return errorMessageList.filtered;
	};

	const handleChangeUrl = () => {
		const url: string | null = search.mapToUrl();
		if (url) navigate(url);
		getErrorMessages();
	};

	const handleSearch = (input?: string) => {
		search.setSearchTerm(input ?? searchTerm);
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const handleChangeId = (id: string | null) => {
		search.id.value = id;
		handleChangeUrl();
	};

	const handleOverlayOpen = (input: string): void => {
		handleChangeId(input);
	};

	const handleOverlayClose = (): void => {
		handleChangeId(null);
	};

	const handleArchiveErrorMessage = async (errorMessage: ErrorMessage): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:ARCHIVE_TITLE'),
			text: t('standingData:ERRORMESSAGE_ARCHIVE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#ff0004',
			confirmButtonColor: '#ff0004',
			confirmButtonText: t('_general:ARCHIVE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await errorMessage.callApiDelete();
				await Swal.fire({
					title: t('standingData:ERRORMESSAGE_ARCHIVE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getErrorMessages();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleRestoreErrorMessage = async (errorMessage: ErrorMessage): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:RESTORE_TITLE'),
			text: t('standingData:ERRORMESSAGE_RESTORE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#00cc07',
			confirmButtonColor: '#00cc07',
			confirmButtonText: t('_general:RESTORE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await errorMessage.callApiRestore();
				await Swal.fire({
					title: t('standingData:ERRORMESSAGE_RESTORE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getErrorMessages();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleChangeIdToNew = (): void => {
		handleChangeId('new');
	};

	const getButtonDataArchive = (input: ErrorMessage): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleArchiveErrorMessage(input),
			};
		}
	};

	const getButtonDataEdit = (id: string): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleOverlayOpen(id),
			};
		}
	};

	const getButtonDataRestore = (input: ErrorMessage): BtnData | undefined => {
		if (props.archive) {
			return {
				onClick: () => handleRestoreErrorMessage(input),
			};
		}
	};

	const renderButtons = (input: ErrorMessage): JSX.Element => {
		return (
			<BtnGroup
				edit={getButtonDataEdit(input.id)}
				archive={getButtonDataArchive(input)}
				restore={getButtonDataRestore(input)}
			/>
		);
	};

	const renderListRows = (): Row[] => {
		return errorMessageList.all.map((errorMessage: ErrorMessage) => {
			return {
				id: errorMessage.id,
				jsx: (
					<div
						className="d-flex flex-row justify-content-start align-items-center"
						style={{ gap: '2em' }}
					>
						<div style={{ width: '500px' }}>
							<span className="grey">{t('standingData:ERROR_KEY')}: </span>
							{errorMessage.errorKey}
						</div>
						<div style={{ width: '900px' }}>
							<span className="grey">{t('_general:NAME')}: </span>
							{errorMessage.errorMessage}
						</div>
						<div>{renderButtons(errorMessage)}</div>
					</div>
				),
			};
		});
	};

	const renderContent = () => {
		return (
			<>
				<main className="container">
					<div className="d-flex flex-row justify-content-between">
						{!props.archive ? (
							<>
								<h1>{t('standingData:ERRORMESSAGE_MANAGEMENT')}</h1>
								<div className="d-flex flex-gap-25">
									<BtnNew
										onClick={handleChangeIdToNew}
										name={t('standingData:CREATE_ERRORMESSAGE')}
									/>
								</div>
							</>
						) : (
							<h1>{t('standingData:ERRORMESSAGE_ARCHIVE')}</h1>
						)}
					</div>
					<br />
					<div className="row">
						<div className="col-lg-12">
							<div className="input-group mb-3">
								<AdvancedSearchInput
									debounceMilliSeconds={350}
									getSuggestedItems={getSuggestedErrorMessages}
									handleSearch={handleSearch}
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									triggerSearchLetterAmount={1}
								/>
							</div>
						</div>
					</div>
					<br />
					<LoadingAnimation isLoading={isLoading} />
					<p>{`${errorMessageList.totalCount} ${t('_general:ERRORMESSAGES')}`}</p>
					<StandingDataList
						archive={props.archive}
						displayOverlay={displayOverlay}
						rows={renderListRows()}
						overlay={<ErrorMessageDetailComponent />}
						handleOverlayOpen={handleOverlayOpen}
						handleOverlayClose={handleOverlayClose}
					/>
					<br />
					<Pagination
						countOfAllListItems={errorMessageList.totalCount}
						pageIndex={search.getPageIndex()}
						setPageIndex={handleChangePageIndex}
						listItemAmount={search.getPageSize()}
						maxNumberOfButtons={maxNumberOfButtons}
					/>
				</main>
			</>
		);
	};

	return <>{renderContent()}</>;
};

export default ErrorMessageManagementComponent;
